/*
 * Copyright (c) Microsoft Corporation.
 * Licensed under the MIT License.
 *
 * Code generated by Microsoft (R) AutoRest Code Generator.
 * Changes may cause incorrect behavior and will be lost if the code is regenerated.
 */

import { PagedAsyncIterableIterator, PageSettings } from "@azure/core-paging";
import { setContinuationToken } from "../pagingHelper";
import { DatabaseSqlVulnerabilityAssessmentScans } from "../operationsInterfaces";
import * as coreClient from "@azure/core-client";
import * as Mappers from "../models/mappers";
import * as Parameters from "../models/parameters";
import { SqlManagementClient } from "../sqlManagementClient";
import {
  SqlVulnerabilityAssessmentScanRecord,
  VulnerabilityAssessmentName,
  DatabaseSqlVulnerabilityAssessmentScansListBySqlVulnerabilityAssessmentsNextOptionalParams,
  DatabaseSqlVulnerabilityAssessmentScansListBySqlVulnerabilityAssessmentsOptionalParams,
  DatabaseSqlVulnerabilityAssessmentScansListBySqlVulnerabilityAssessmentsResponse,
  DatabaseSqlVulnerabilityAssessmentScansGetOptionalParams,
  DatabaseSqlVulnerabilityAssessmentScansGetResponse,
  DatabaseSqlVulnerabilityAssessmentScansListBySqlVulnerabilityAssessmentsNextResponse
} from "../models";

/// <reference lib="esnext.asynciterable" />
/** Class containing DatabaseSqlVulnerabilityAssessmentScans operations. */
export class DatabaseSqlVulnerabilityAssessmentScansImpl
  implements DatabaseSqlVulnerabilityAssessmentScans {
  private readonly client: SqlManagementClient;

  /**
   * Initialize a new instance of the class DatabaseSqlVulnerabilityAssessmentScans class.
   * @param client Reference to the service client
   */
  constructor(client: SqlManagementClient) {
    this.client = client;
  }

  /**
   * Lists the vulnerability assessment scans of a database.
   * @param resourceGroupName The name of the resource group that contains the resource. You can obtain
   *                          this value from the Azure Resource Manager API or the portal.
   * @param serverName The name of the server.
   * @param databaseName The name of the database.
   * @param vulnerabilityAssessmentName The name of the vulnerability assessment.
   * @param options The options parameters.
   */
  public listBySqlVulnerabilityAssessments(
    resourceGroupName: string,
    serverName: string,
    databaseName: string,
    vulnerabilityAssessmentName: VulnerabilityAssessmentName,
    options?: DatabaseSqlVulnerabilityAssessmentScansListBySqlVulnerabilityAssessmentsOptionalParams
  ): PagedAsyncIterableIterator<SqlVulnerabilityAssessmentScanRecord> {
    const iter = this.listBySqlVulnerabilityAssessmentsPagingAll(
      resourceGroupName,
      serverName,
      databaseName,
      vulnerabilityAssessmentName,
      options
    );
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listBySqlVulnerabilityAssessmentsPagingPage(
          resourceGroupName,
          serverName,
          databaseName,
          vulnerabilityAssessmentName,
          options,
          settings
        );
      }
    };
  }

  private async *listBySqlVulnerabilityAssessmentsPagingPage(
    resourceGroupName: string,
    serverName: string,
    databaseName: string,
    vulnerabilityAssessmentName: VulnerabilityAssessmentName,
    options?: DatabaseSqlVulnerabilityAssessmentScansListBySqlVulnerabilityAssessmentsOptionalParams,
    settings?: PageSettings
  ): AsyncIterableIterator<SqlVulnerabilityAssessmentScanRecord[]> {
    let result: DatabaseSqlVulnerabilityAssessmentScansListBySqlVulnerabilityAssessmentsResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listBySqlVulnerabilityAssessments(
        resourceGroupName,
        serverName,
        databaseName,
        vulnerabilityAssessmentName,
        options
      );
      let page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listBySqlVulnerabilityAssessmentsNext(
        resourceGroupName,
        serverName,
        databaseName,
        vulnerabilityAssessmentName,
        continuationToken,
        options
      );
      continuationToken = result.nextLink;
      let page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listBySqlVulnerabilityAssessmentsPagingAll(
    resourceGroupName: string,
    serverName: string,
    databaseName: string,
    vulnerabilityAssessmentName: VulnerabilityAssessmentName,
    options?: DatabaseSqlVulnerabilityAssessmentScansListBySqlVulnerabilityAssessmentsOptionalParams
  ): AsyncIterableIterator<SqlVulnerabilityAssessmentScanRecord> {
    for await (const page of this.listBySqlVulnerabilityAssessmentsPagingPage(
      resourceGroupName,
      serverName,
      databaseName,
      vulnerabilityAssessmentName,
      options
    )) {
      yield* page;
    }
  }

  /**
   * Lists the vulnerability assessment scans of a database.
   * @param resourceGroupName The name of the resource group that contains the resource. You can obtain
   *                          this value from the Azure Resource Manager API or the portal.
   * @param serverName The name of the server.
   * @param databaseName The name of the database.
   * @param vulnerabilityAssessmentName The name of the vulnerability assessment.
   * @param options The options parameters.
   */
  private _listBySqlVulnerabilityAssessments(
    resourceGroupName: string,
    serverName: string,
    databaseName: string,
    vulnerabilityAssessmentName: VulnerabilityAssessmentName,
    options?: DatabaseSqlVulnerabilityAssessmentScansListBySqlVulnerabilityAssessmentsOptionalParams
  ): Promise<
    DatabaseSqlVulnerabilityAssessmentScansListBySqlVulnerabilityAssessmentsResponse
  > {
    return this.client.sendOperationRequest(
      {
        resourceGroupName,
        serverName,
        databaseName,
        vulnerabilityAssessmentName,
        options
      },
      listBySqlVulnerabilityAssessmentsOperationSpec
    );
  }

  /**
   * Get a database vulnerability assessment scan result.
   * @param resourceGroupName The name of the resource group that contains the resource. You can obtain
   *                          this value from the Azure Resource Manager API or the portal.
   * @param serverName The name of the server.
   * @param databaseName The name of the database.
   * @param vulnerabilityAssessmentName The name of the vulnerability assessment.
   * @param scanId The vulnerability assessment scan Id of the scan to retrieve.
   * @param options The options parameters.
   */
  get(
    resourceGroupName: string,
    serverName: string,
    databaseName: string,
    vulnerabilityAssessmentName: VulnerabilityAssessmentName,
    scanId: string,
    options?: DatabaseSqlVulnerabilityAssessmentScansGetOptionalParams
  ): Promise<DatabaseSqlVulnerabilityAssessmentScansGetResponse> {
    return this.client.sendOperationRequest(
      {
        resourceGroupName,
        serverName,
        databaseName,
        vulnerabilityAssessmentName,
        scanId,
        options
      },
      getOperationSpec
    );
  }

  /**
   * ListBySqlVulnerabilityAssessmentsNext
   * @param resourceGroupName The name of the resource group that contains the resource. You can obtain
   *                          this value from the Azure Resource Manager API or the portal.
   * @param serverName The name of the server.
   * @param databaseName The name of the database.
   * @param vulnerabilityAssessmentName The name of the vulnerability assessment.
   * @param nextLink The nextLink from the previous successful call to the
   *                 ListBySqlVulnerabilityAssessments method.
   * @param options The options parameters.
   */
  private _listBySqlVulnerabilityAssessmentsNext(
    resourceGroupName: string,
    serverName: string,
    databaseName: string,
    vulnerabilityAssessmentName: VulnerabilityAssessmentName,
    nextLink: string,
    options?: DatabaseSqlVulnerabilityAssessmentScansListBySqlVulnerabilityAssessmentsNextOptionalParams
  ): Promise<
    DatabaseSqlVulnerabilityAssessmentScansListBySqlVulnerabilityAssessmentsNextResponse
  > {
    return this.client.sendOperationRequest(
      {
        resourceGroupName,
        serverName,
        databaseName,
        vulnerabilityAssessmentName,
        nextLink,
        options
      },
      listBySqlVulnerabilityAssessmentsNextOperationSpec
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const listBySqlVulnerabilityAssessmentsOperationSpec: coreClient.OperationSpec = {
  path:
    "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Sql/servers/{serverName}/databases/{databaseName}/sqlVulnerabilityAssessments/{vulnerabilityAssessmentName}/scans",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.SqlVulnerabilityAssessmentScanRecordListResult
    },
    default: {}
  },
  queryParameters: [Parameters.apiVersion4],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.serverName,
    Parameters.databaseName,
    Parameters.vulnerabilityAssessmentName
  ],
  headerParameters: [Parameters.accept],
  serializer
};
const getOperationSpec: coreClient.OperationSpec = {
  path:
    "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Sql/servers/{serverName}/databases/{databaseName}/sqlVulnerabilityAssessments/{vulnerabilityAssessmentName}/scans/{scanId}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.SqlVulnerabilityAssessmentScanRecord
    },
    default: {}
  },
  queryParameters: [Parameters.apiVersion4],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.serverName,
    Parameters.databaseName,
    Parameters.vulnerabilityAssessmentName,
    Parameters.scanId
  ],
  headerParameters: [Parameters.accept],
  serializer
};
const listBySqlVulnerabilityAssessmentsNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.SqlVulnerabilityAssessmentScanRecordListResult
    },
    default: {}
  },
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.serverName,
    Parameters.databaseName,
    Parameters.nextLink,
    Parameters.vulnerabilityAssessmentName
  ],
  headerParameters: [Parameters.accept],
  serializer
};
